import { Ticker } from "pixi.js";
import { Stage, Sprite, useTick } from "@pixi/react";
import React, { useEffect, useState } from "react";
import ApiContent from "../../services/Content";
import { getSelectedLanguage } from "../../services/Language";
import { Tween, Group } from "tweedle.js";

import LocalStorageService from "../../services/LocalStorage";
import GameStage from "./GameStage";
import GameBoard from "./GameBoard";
import DiceModal from "../DiceModal";
import Box from "@mui/material/Box";
import {
  Content,
  CommonContent,
  ContentType,
  Language,
} from "../../types/Types";
import { getScreenScale } from "./../../util/GameBoard";
import { MaxScreenSize } from "./../../config/GameBoard";
import DateUtil from "../../util/Date";
import BoardPosition from "./BoardPosition";
import ImgBoard from "./../../assets/images/board/board1024px.jpg";
import ImgBoardFr from "./../../assets/images/board/board1024px_fr.jpg";
import ImgBoardEn from "./../../assets/images/board/board1024px_en.jpg";
import "./styles.scss";
import {
  Player,
  ISprite,
  Position,
  PlayerDetails,
  GameDetails,
  GameStatus,
  UserAccount,
  PositionAction,
} from "../../types/Game";
import GameCard from "./GameCard";
import PlayerService from "../../services/PlayerService";
import Firebase from "../../services/Firebase";
import GameHelper from "../../services/GameHelper";
import GameApi from "../../services/GameApi";
import StartMp3Sound from "./../../assets/sounds/inicio-juego-flute_78_d-104176.mp3";
import DiceTurnSound from "./../../assets/sounds/turn_notification.wav";
import GameOverSound from "./../../assets/sounds/final-juego-success-fanfare-trumpets-6185.mp3";
import RighAnswerSound from "./../../assets/sounds/respuesta-correcta-cartoon-142268.mp3";
import WrongAnswerSound from "./../../assets/sounds/respuesta-errada-negative_beeps-6008.mp3";
import SlideSound from "./../../assets/sounds/tobogan-fx-swanee-whistle-down-42035.mp3";
import MoveSound from "./../../assets/sounds/mover-casilla.mp3";
import MoveToStrtSound from "./../../assets/sounds/vuelve-al-inicio-2.mp3";
import FinalPositionSound from "./../../assets/sounds/jugadora-llega-al-final-short-crowd-cheer-6713.mp3";
import ladderSound from "./../../assets/sounds/escalera-cartoon-jump-6462.mp3";
import { useGlobalAudioPlayer } from "react-use-audio-player";
import GenericUtil from "./../../util/Generic";
import { FINAL_BOARD_POSITION } from "../../config/Constants";

const SINGLE_DICE_POSITION = 90;

type Props = {
  content: Content[];
  commonContent: CommonContent[];
  players: PlayerDetails[];
  game: GameDetails;
  user: UserAccount;
  gameOver: boolean;
};

const getScreenSize = () => ({
  width: window.innerWidth,
  height: window.innerHeight,
});

const useResize = () => {
  const [size, setSize] = useState(getScreenSize());

  React.useEffect(() => {
    const onResize = () => setSize(getScreenSize());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  if (size.width >= MaxScreenSize.MaxtWidth) {
    return { width: MaxScreenSize.MaxtWidth, height: MaxScreenSize.MaxHeight };
  }

  return size;
};

const Game = (props: Props) => {
  const { load } = useGlobalAudioPlayer();
  const dimensions = useResize();
  const scale = getScreenScale(dimensions.width);
  const [customPosition, setCustomPosition] = useState(0);
  const [positionIndex, setPositionIndex] = useState(1);
  const [currentPlayers, setCurrentPlayers] = useState<Player[]>(
    LocalStorageService.getGamePlayers() || []
  );
  const [sprites, setSprites] = useState([
    React.useRef<ISprite>(null),
    React.useRef<ISprite>(null),
    React.useRef<ISprite>(null),
    React.useRef<ISprite>(null),
    React.useRef<ISprite>(null),
  ]);
  const [lastMove, setLastMove] = useState<Position>({ x: 0, y: 0 });
  const [lastCard, setLastCard] = useState<Content>();
  const [rollDice, setRollDice] = React.useState<boolean>(false);
  const [startedGame, setStartedGame] = React.useState<boolean>(false);

  console.log("Initial state players", currentPlayers);

  const checkLinkedPosition = async (
    player: Player,
    positionNumber: number
  ) => {
    const linkedPosition = BoardPosition.getLinked(positionNumber);
    if (linkedPosition) {
      const position = BoardPosition.get(linkedPosition.to);
      const sprite = sprites[player.spriteId];
      if (position && sprite) {
        console.log("Link:", position);
        setTimeout(() => {
          playSoundPlayerPosition(linkedPosition.type);
          refreshPlayerPosition(player, position);
          new Tween(sprite.current?.scale)
            .to({ x: 0.3 * scale, y: 0.3 * scale }, 1000)
            .repeat(3)
            .yoyo(true)
            .start();
          new Tween(sprite.current)
            .to({ x: position.x * scale, y: position.y * scale }, 500)
            .start();
        }, 2000);
      }
    }
  };

  useTick((delta) => {
    Ticker.shared.add(update);
  });

  useEffect(() => {
    if (!startedGame) {
      load(StartMp3Sound, {
        autoplay: true,
      });
      setStartedGame(true);
    }
  });

  useEffect(() => {
    loadPlayers();
  }, [sprites, props.players]);

  useEffect(() => {
  }, [props.user]);

  useEffect(() => {
    showThrowDice();
    setCardContent();
  }, [props.game, props.user]);

  useEffect(() => {
    playFinalPositionSound(props.game);
  }, [props.players,props.game]);

  useEffect(() => {
    closeCard();
  });

  useEffect(() => {
    if (props.gameOver && props.user.userId === props.game.turnUserId) {
      GameApi.addPlayersToGame(props.game.gameId, currentPlayers)
        .then((r) => {
          console.log(r);
        })
        .catch((e) => console.error);
    }
    if (props.gameOver) {
      load(GameOverSound, {
        autoplay: true,
      });
    }
  }, [props.gameOver]);

  const update = () => {
    Group.shared.update();
  };

  const loadPlayers = () => {
    const players = GameStage.createPlayers(props.players);
    console.log("Current players");
    console.log(players);
    setCurrentPlayers(players);
  };

  const movePlayer = (positionNumber: number, index: number) => {
    console.log("Move player to:");
    const sprite = sprites[index];
    const position = BoardPosition.get(positionNumber);
    const player = GameStage.getPlayerBySpriteId(index, currentPlayers);
    console.log(position);

    if (sprite && sprite.current && position && player) {
      refreshPlayerPosition(player, position);
      setLastMove(position);
      new Tween(sprite.current)
        .to({ x: position.x * scale, y: position.y * scale }, 500)
        .start();
      checkLinkedPosition(player, positionNumber);
    }
  };

  const moveToInitialPosition = (player: Player) => {
    const position = BoardPosition.getStart(player.spriteId);
    const sprite = sprites[player.spriteId];
    if (sprite && sprite.current && position) {
      load(MoveToStrtSound, {
        autoplay: true,
      });
      refreshPlayerPosition(player, position);
      new Tween(sprite.current)
        .to({ x: position.x * scale, y: position.y * scale }, 500)
        .start();
    }
  };

  const refreshPlayerPosition = (player: Player, position: Position) => {
    console.log("refreshPlayerPosition", position);
    const index = BoardPosition.getPositionNumber(position);
    console.log("Moving to index:", index);
    updateRemotePlayerPosition(index, player);
  };

  const checkOtherPlayerOnNextPosition = (
    position: Position,
    players: Array<Player>
  ) => {
    const player = GameBoard.getPlayerInPosition(position, players);
    if (player) {
      setTimeout(() => {
        moveToInitialPosition(player);
      }, 2500);
    }
  };

  const movePlayerTo = (positionNumber: number, index: number) => {
    movePlayer(positionNumber, index);
    load(MoveSound, {
      autoplay: true,
    });
    setTimeout(() => {
      getPositionCard(positionNumber);
    }, 3500);

    const position = BoardPosition.getFinalPosition(positionNumber);
  };

  const getDiceResults = async (diceNumbers: number[]) => {
    //const diceNumbers =[1,1]
    console.log("diceNumbers", diceNumbers);
    await Firebase.updateGame(props.game.gameId, {
      diceResult: diceNumbers,
      turnUserLocation: { x: 0, y: 0 },
    });
    setTimeout(async () => {
      const moveTo = PlayerService.getTotal(diceNumbers);

      const playerThrowing = PlayerService.getPlayerTrowingDice(
        currentPlayers,
        props.game
      );
      console.log("playerThrowing", playerThrowing);
      if (playerThrowing) {
        const endingPosition = GameHelper.getDiceEndingPosition(
          diceNumbers,
          playerThrowing.profile.boardPosition,
          props.game.mode
        );
        const newPosition = endingPosition
          ? endingPosition
          : moveTo + playerThrowing.profile.boardPosition;

        await checkMagnifyingGlass(playerThrowing.profile, diceNumbers);
        console.log("moveTo", newPosition);
        movePlayerTo(newPosition, playerThrowing.spriteId);
      }

      setRollDice(false);
    }, 4000);
  };

  const getPositionCard = async (positionNumber: number) => {
    const position = BoardPosition.getFinalPosition(positionNumber);
    if (position?.color) {
      const card = await GameStage.getRandomCard(props.content, position.color);
      await Firebase.updateGame(props.game.gameId, {
        status: GameStatus.ReadingCard,
        cardId: card.id,
        turnUserLocation: position,
      });
    } else {
      moveToNextPlayer();
    }
  };

  const setCardContent = async () => {
    if (props.game.status === GameStatus.ReadingCard && props.game.cardId) {
      const card = ApiContent.findById(props.content, props.game.cardId);
      if (card) {
        setLastCard(card);
      }
    }
  };

  const closeCard = () => {
    if (props.game.status === GameStatus.RollDice) {
      setLastCard(undefined);
    }
  };

  const moveToNextPlayer = async () => {
    const nextPlayer = PlayerService.getNextPlayerToThrowDice(
      currentPlayers,
      props.game
    );

    await Firebase.updateGame(props.game.gameId, {
      status: GameStatus.RollDice,
      diceResult: [],
      turnUserId: nextPlayer?.profile.userId,
      cardId: "",
      cardAnswer: "",
    });

    console.log("Next Player to move:", nextPlayer);
  };

  const showThrowDice = () => {
    if (GameStatus.RollDice === props.game.status) {
      if (
        props.game.turnUserPosition &&
        props.game.turnUserPosition < FINAL_BOARD_POSITION
      ) {
        load(DiceTurnSound, {
          autoplay: true,
        });
      }
      const userRollDice = props.game.turnUserId === props.user.userId;
      console.log("userRollDice:", userRollDice);
      setRollDice(userRollDice);
    }
  };

  const playFinalPositionSound = (game: GameDetails) => {

    const player = PlayerService.getPlayer(
      props.players,
      props.game.turnUserId
    );
   

    if(!player){
      return;
    }
  
 
    if (player.boardPosition && player.boardPosition === FINAL_BOARD_POSITION) {
      playSoundOnFinishing();
      //alert("Finished");
    }
  };

  const updateRemotePlayerPosition = async (
    boardPosition: number,
    player?: Player
  ) => {
    const hasFinished = PlayerService.hasPlayerReachedFinalPosition(player);

    const points =
      hasFinished && player?.profile.points
        ? [
            ...player?.profile.points,
            PlayerService.getEndingGamePlayerPoints(props.players),
          ]
        : player?.profile.points;

    await Firebase.updateGamePlayer(
      props.game.gameId,
      player?.profile.userId || props.user.userId,
      {
        boardPosition,
        finishTimeStamp: hasFinished ? DateUtil.getTimeStamp() : 0,
        points,
      }
    );
  };

  const isUsersTurn = () => {
    return props.game.turnUserId === props.user.userId;
  };

  const onTimeOut = async () => {
    if (!isUsersTurn) {
      console.log("It is not user's turn:", props.user);
      return;
    }
    await convertGreenCardRatingToPoints();
    setLastCard(undefined);
    moveToNextPlayer();
  };

  const convertGreenCardRatingToPoints = async (): Promise<void> => {
    if (lastCard?.type === ContentType.GREEN_CARDS) {
      const res = await GameHelper.getGreenCardScore(
        lastCard.id,
        props.game.gameId,
        props.game.turnUserId
      );
      const player = PlayerService.getPlayerTrowingDice(
        currentPlayers,
        props.game
      );
      if (player) {
        await PlayerService.addPlayerPoints(player, res.total);
      }
    }
  };

  const onCardAnswer = async (cardAnswer: string, isRight: boolean) => {
    console.log("cardAnswer:", cardAnswer);
    console.log("isRight:", isRight);
    if (!isUsersTurn) {
      console.log("It is not user's turn:", props.user);
      return;
    }
    await Firebase.updateGame(props.game.gameId, {
      cardAnswer,
    });

    if (isRight !== undefined) {
      await updatePoints(isRight ? 1 : 0);
    }

    setTimeout(() => {
      moveToNextPlayer();
    }, 3000);
  };

  const playSoundOnCardAnswer = (isRight: number) => {
    load(isRight >= 1 ? RighAnswerSound : WrongAnswerSound, {
      autoplay: true,
    });
  };

  const playSoundOnFinishing = () => {
      load(FinalPositionSound, {
        autoplay: true,
      });
  };

  const playSoundPlayerPosition = (type: string) => {
    load(type === PositionAction.Ladder ? ladderSound : SlideSound, {
      autoplay: true,
    });
  };

  const updatePoints = async (points: number) => {
    playSoundOnCardAnswer(points);
    const player = PlayerService.getPlayerTrowingDice(
      currentPlayers,
      props.game
    );

    if (player) {
      await PlayerService.addPlayerPoints(player, points);
    }
  };

  const onGreenCardAnswer = async (cardAnswer: string) => {
    if (!isUsersTurn) {
      console.log("It is not user's turn:", props.user);
      return;
    }
    await Firebase.updateGame(props.game.gameId, {
      cardAnswer,
      tempCardAnswer: "",
    });
  };

  const onGreenCardTyping = async (tempCardAnswer: string) => {
    if (!isUsersTurn) {
      console.log("It is not user's turn:", props.user);
      return;
    }
    await Firebase.updateGame(props.game.gameId, {
      tempCardAnswer,
    });
  };

  const getNumberOfDices = (): number => {
    const currentPosition = PlayerService.getPlayerBoardPosition(
      currentPlayers,
      props.game
    );
    console.log("Current postion:", currentPosition);
    return currentPosition && currentPosition >= SINGLE_DICE_POSITION ? 1 : 2;
  };

  const checkMagnifyingGlass = async (
    player: PlayerDetails,
    dice: number[]
  ) => {
    if (PlayerService.isDicePaired(dice)) {
      await PlayerService.addPlayerMagnfyingGlass(player, 1);
    }
  };

  const onUseMagnifyingGlass = async () => {
    const player = await PlayerService.getPlayer(
      props.players,
      props.game.turnUserId
    );
    if (player) {
      await PlayerService.addPlayerMagnfyingGlass(player, -1);
    }
  };

  const reposition = (position: Position, players: Player[]) => {
    const occupated = GameBoard.getPlayerInPosition(position, players);

    if (!occupated) {
      return position;
    }

    return {
      x: GenericUtil.getRandomNumber(position.x + 10, position.x - 10),
      y: GenericUtil.getRandomNumber(position.y + 10, position.y - 10),
    };
  };

  const getBackGround = () => {
    const lang = getSelectedLanguage();
    return lang === Language.ENGLISH
      ? ImgBoardEn
      : lang === Language.FRENCH
      ? ImgBoardFr
      : ImgBoard;
  };

  console.log("Sprites");
  console.log(sprites);
  console.log("Current players");
  console.log(currentPlayers);

  return (
    <Box>
      <Stage
        width={dimensions.width}
        height={dimensions.width}
        style={{ margin: 0 }}
        options={{ backgroundColor: 0x78abac }}
      >
        <Sprite
          scale={{ x: scale, y: scale }}
          image={getBackGround()}
          x={0}
          y={0}
          width={1024 * scale}
          height={1024 * scale}
        />
        {currentPlayers.map((player) => (
          <Sprite
            ref={sprites[player.spriteId]}
            image={player.profile.profileUrl}
            x={reposition(player.position, currentPlayers).x * scale}
            y={reposition(player.position, currentPlayers).y * scale}
            width={55 * scale}
            height={55 * scale}
            interactive={true}
            pointertap={() => {}}
          />
        ))}
      </Stage>
      {props.game.turnUserLocation?.color && lastCard && (
        <GameCard
          magnifyingGlasses={PlayerService.calCulateMagnfyingGlass(
            props.players,
            props.user
          )}
          players={currentPlayers}
          user={props.user}
          onGreenCardTyping={onGreenCardTyping}
          onGreenCardAnswer={onGreenCardAnswer}
          game={props.game}
          onCardAnswer={onCardAnswer}
          commonContent={props.commonContent}
          content={props.content}
          cardContent={lastCard}
          cardColor={props.game.turnUserLocation?.color}
          isUsersTurn={isUsersTurn()}
          onUseMagnifyingGlass={onUseMagnifyingGlass}
          onTimeOut={() => {
            onTimeOut();
          }}
        />
      )}

      <DiceModal
        game={props.game}
        content={props.commonContent}
        user={props.user}
        diceNumber={getNumberOfDices()}
        onDiceStop={getDiceResults}
        timeSpining={5000}
        open={rollDice && props.gameOver !== true}
        onTimeOut={onTimeOut}
      />
    </Box>
  );
};

export default Game;
